<template>
    <PanelCourses
        :is-loading="isLoading"
        :courses="courses"
        :searched-phrase="term"
        :has-more="hasMore"
        @load-more="search"
        ><template
            v-if="!courses.length && !isLoading"
            #footer
        >
            <h4
                class="font-body-2-medium pb-1 break-words"
                v-text="
                    searchedPhrase
                        ? $t('search.no_courses_title', { phrase: searchedPhrase })
                        : $t('search.no_courses_title_default')
                "
            ></h4>
            <ul class="text-slate-600 pl-5 list-disc font-body-2-regular">
                <li
                    v-for="(text, index) in noResultTexts"
                    :key="index"
                    v-text="text"
                />
            </ul> </template
    ></PanelCourses>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import PanelCourses from '@/components/PanelCourses';
import { searchCourses } from '@/api/gateway/legacy-api/search';
import type { SearchCourseResource } from '@/api/gateway/legacy-api/resources/SearchCourseResource';
import { isEqual } from 'lodash-es';
import type { InstitutionType } from '@/api/gateway/legacy-api/resources/UniversityResource';
import { TrackingContentType } from './SearchIndex';

export default defineComponent({
    name: 'CourseResults',
    components: {
        PanelCourses,
    },
    i18nOptions: {
        namespaces: ['common'],
    },
    props: {
        /**
         * The term to search for courses
         */
        term: {
            type: String,
            default: '',
        },
        /**
         * University IDs to be applied on courses search results.
         */
        universityIds: {
            type: Array<number>,
            default: [],
        },
        /**
         * Searched phrase, to display in no results message
         */
        searchedPhrase: {
            type: String,
            default: undefined,
        },
        /**
         * Institution type to be applied on courses search results.
         */
        institutionType: {
            type: Number as PropType<InstitutionType>,
            default: undefined,
        },
    },
    emits: ['search-finished'],
    data() {
        return {
            page: 1,
            hasMore: true,
            isLoading: true as boolean,
            courses: [] as SearchCourseResource[],
        };
    },
    computed: {
        noResultTexts() {
            return this.$t('search.no_courses_description', { returnObjects: true });
        },
    },
    watch: {
        term() {
            this.reset();
            this.search();
        },
        institutionType() {
            this.reset();
            this.search();
        },
        universityIds(universityIds, oldUniversityIds) {
            if (isEqual(universityIds, oldUniversityIds)) return;
            this.reset();
            this.search();
        },
    },
    mounted() {
        this.search();
    },
    methods: {
        reset() {
            this.page = 1;
            this.hasMore = true;
            this.courses = [];
            this.isLoading = true;
        },
        search() {
            this.isLoading = true;
            const perPage = this.page === 1 ? 9 : 6;
            searchCourses({
                term: this.term,
                per_page: perPage,
                page: this.page,
                university_id: this.universityIds,
                institution_type: this.institutionType,
            }).then((response) => {
                this.courses = [...this.courses, ...response.data.courses.results];
                this.hasMore = response.data.courses.pagination.has_more;
                this.$emit('search-finished', response.data.courses.pagination.total, TrackingContentType.Courses);
                this.isLoading = false;
                this.page += 1;
            });
        },
    },
});
</script>
